// Intake.AI - Main Application Page
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import TrialBanner from '../components/TrialBanner';
import PullToRefresh from '../components/PullToRefresh';
import { IntakeSMSComposer, IntakeSMSSettings } from '../components/IntakeSMS';
import {
  Scale,
  UserPlus,
  Users,
  Phone,
  Mail,
  Building,
  Calendar,
  ChevronLeft,
  LayoutGrid,
  Plus,
  Search,
  Filter,
  MoreVertical,
  ArrowRight,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  GripVertical,
  Mic,
  FileText,
  Send,
  Link as LinkIcon,
  Copy,
  ExternalLink,
  Loader2,
  Settings,
  MessageSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pipeline stage colors and icons
const STAGE_CONFIG = {
  new_lead: { color: 'bg-blue-500', textColor: 'text-blue-700', bgLight: 'bg-blue-50' },
  contacted: { color: 'bg-purple-500', textColor: 'text-purple-700', bgLight: 'bg-purple-50' },
  consultation_scheduled: { color: 'bg-amber-500', textColor: 'text-amber-700', bgLight: 'bg-amber-50' },
  consultation_complete: { color: 'bg-emerald-500', textColor: 'text-emerald-700', bgLight: 'bg-emerald-50' },
  agreement_sent: { color: 'bg-indigo-500', textColor: 'text-indigo-700', bgLight: 'bg-indigo-50' },
  signed_converted: { color: 'bg-green-500', textColor: 'text-green-700', bgLight: 'bg-green-50' },
  declined: { color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50' }
};

export default function IntakeAppPage({ embedded = false }) {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [showNewContactDialog, setShowNewContactDialog] = useState(false);
  const [showNewLeadDialog, setShowNewLeadDialog] = useState(false);
  const [newContact, setNewContact] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    tags: []
  });
  const [newLead, setNewLead] = useState({
    contact_id: '',
    source: 'website',
    case_type: '',
    case_description: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [draggedLead, setDraggedLead] = useState(null);
  
  // Client Intake Link state
  const [showIntakeLinkDialog, setShowIntakeLinkDialog] = useState(false);
  const [intakeLinks, setIntakeLinks] = useState([]);
  const [generatingIntakeLink, setGeneratingIntakeLink] = useState(false);
  
  // SMS state
  const [showSMSDialog, setShowSMSDialog] = useState(false);
  const [smsLead, setSmsLead] = useState(null);
  const [showSMSSettings, setShowSMSSettings] = useState(false);

  useEffect(() => {
    fetchData();
    fetchIntakeLinks();
  }, [token]);

  const fetchData = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Fetch config
      const configRes = await fetch(`${API_URL}/api/intake/config`, { headers });
      if (configRes.ok) setConfig(await configRes.json());
      
      // Fetch pipeline
      const pipelineRes = await fetch(`${API_URL}/api/intake/leads/pipeline`, { headers });
      if (pipelineRes.ok) setPipeline(await pipelineRes.json());
      
      // Fetch stats
      const statsRes = await fetch(`${API_URL}/api/intake/stats`, { headers });
      if (statsRes.ok) setStats(await statsRes.json());
      
      // Fetch contacts
      const contactsRes = await fetch(`${API_URL}/api/intake/contacts`, { headers });
      if (contactsRes.ok) setContacts(await contactsRes.json());
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load intake data');
    } finally {
      setLoading(false);
    }
  };

  const fetchIntakeLinks = async () => {
    try {
      const res = await fetch(`${API_URL}/api/intake/intake-links`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setIntakeLinks(await res.json());
      }
    } catch (error) {
      console.error('Error fetching intake links:', error);
    }
  };

  const generateIntakeLink = async () => {
    setGeneratingIntakeLink(true);
    try {
      const res = await fetch(`${API_URL}/api/intake/intake-links`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          expires_in_days: 14,
          max_files: 10
        })
      });
      
      if (res.ok) {
        const link = await res.json();
        setIntakeLinks([link, ...intakeLinks]);
        toast.success('Client intake link generated');
        setShowIntakeLinkDialog(true);
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to generate link');
      }
    } catch (error) {
      toast.error('Failed to generate intake link');
    } finally {
      setGeneratingIntakeLink(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied to clipboard');
  };

  const deactivateIntakeLink = async (linkId) => {
    try {
      const res = await fetch(`${API_URL}/api/intake/intake-links/${linkId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setIntakeLinks(intakeLinks.map(l => 
          l.id === linkId ? { ...l, is_active: false } : l
        ));
        toast.success('Intake link deactivated');
      }
    } catch (error) {
      toast.error('Failed to deactivate link');
    }
  };

  const createContact = async () => {
    try {
      const res = await fetch(`${API_URL}/api/intake/contacts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newContact)
      });
      
      if (res.ok) {
        const contact = await res.json();
        setContacts([contact, ...contacts]);
        setShowNewContactDialog(false);
        setNewContact({ first_name: '', last_name: '', email: '', phone: '', tags: [] });
        toast.success('Contact created successfully');
        return contact;
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create contact');
      }
    } catch (error) {
      toast.error('Failed to create contact');
    }
    return null;
  };

  const createLead = async () => {
    try {
      const res = await fetch(`${API_URL}/api/intake/leads`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newLead)
      });
      
      if (res.ok) {
        toast.success('Lead created successfully');
        setShowNewLeadDialog(false);
        setNewLead({ contact_id: '', source: 'website', case_type: '', case_description: '' });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create lead');
      }
    } catch (error) {
      toast.error('Failed to create lead');
    }
  };

  const moveLeadToStage = async (leadId, newStage) => {
    try {
      const res = await fetch(`${API_URL}/api/intake/leads/${leadId}/stage`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ stage: newStage })
      });
      
      if (res.ok) {
        fetchData();
        toast.success('Lead moved successfully');
      }
    } catch (error) {
      toast.error('Failed to move lead');
    }
  };

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, stageId) => {
    e.preventDefault();
    if (draggedLead && draggedLead.stage !== stageId) {
      moveLeadToStage(draggedLead.id, stageId);
    }
    setDraggedLead(null);
  };

  // Refresh handler for pull-to-refresh - must be before any returns
  const handleRefresh = useCallback(async () => {
    await Promise.all([fetchData(), fetchIntakeLinks()]);
    toast.success('Data refreshed');
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-center">
          <UserPlus className="w-12 h-12 text-bronze mx-auto mb-4" />
          <p className="text-muted-foreground">Loading Intake.AI...</p>
        </div>
      </div>
    );
  }

  const stages = config?.pipeline_stages || [];
  const leadsByStage = pipeline?.leads_by_stage || {};

  const intakeBody = (
    <>
        {embedded && (
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-heading font-semibold">Client Intake Pipeline</h3>
              <p className="text-sm text-muted-foreground">Lead tracking, consultations & client conversion</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowNewContactDialog(true)}>
                <Users className="w-4 h-4 mr-1" /> New Contact
              </Button>
              <Button size="sm" onClick={() => setShowNewLeadDialog(true)}>
                <Plus className="w-4 h-4 mr-1" /> New Lead
              </Button>
            </div>
          </div>
        )}

      {/* Stats Bar */}
      <div className="bg-white border-b px-4 lg:px-8 py-3 sm:py-4">
        <div className="flex items-center gap-4 sm:gap-6 overflow-x-auto">
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xl sm:text-2xl font-bold">{stats?.total_leads || 0}</p>
              <p className="text-xs text-muted-foreground">Total Leads</p>
            </div>
          </div>
          <div className="h-8 w-px bg-border flex-shrink-0" />
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
            </div>
            <div>
              <p className="text-xl sm:text-2xl font-bold">{stats?.by_stage?.signed_converted || 0}</p>
              <p className="text-xs text-muted-foreground">Converted</p>
            </div>
          </div>
          <div className="h-8 w-px bg-border flex-shrink-0" />
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xl sm:text-2xl font-bold">{stats?.conversion_rate || 0}%</p>
              <p className="text-xs text-muted-foreground">Conversion</p>
            </div>
          </div>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="p-4 lg:p-8 overflow-x-auto">
        <div className="flex gap-3 sm:gap-4 min-w-max">
          {stages.map((stage) => {
            const stageConfig = STAGE_CONFIG[stage.id] || STAGE_CONFIG.new_lead;
            const leads = leadsByStage[stage.id] || [];
            
            return (
              <div
                key={stage.id}
                className="w-64 sm:w-80 flex-shrink-0"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage.id)}
              >
                {/* Column Header */}
                <div className={`rounded-t-lg px-4 py-3 ${stageConfig.bgLight} border border-b-0`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${stageConfig.color}`} />
                      <h3 className={`font-semibold ${stageConfig.textColor}`}>{stage.name}</h3>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {leads.length}
                    </Badge>
                  </div>
                </div>

                {/* Column Content */}
                <div className="bg-slate-100 border border-t-0 rounded-b-lg p-2 min-h-[500px] space-y-2">
                  {leads.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      No leads in this stage
                    </div>
                  ) : (
                    leads.map((lead) => (
                      <Card
                        key={lead.id}
                        className="cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow"
                        draggable
                        onDragStart={(e) => handleDragStart(e, lead)}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <GripVertical className="w-4 h-4 text-muted-foreground" />
                              <h4 className="font-medium text-sm">
                                {lead.contact?.full_name || 'Unknown Contact'}
                              </h4>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => navigate(`/intake/leads/${lead.id}`)}>
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate(`/intake/leads/${lead.id}/consultation`)}>
                                  <Mic className="w-4 h-4 mr-2" />
                                  Record Consultation
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate(`/intake/leads/${lead.id}/agreement`)}>
                                  <FileText className="w-4 h-4 mr-2" />
                                  Send Agreement
                                </DropdownMenuItem>
                                {lead.contact?.phone && (
                                  <DropdownMenuItem onClick={() => {
                                    setSmsLead(lead);
                                    setShowSMSDialog(true);
                                  }}>
                                    <MessageSquare className="w-4 h-4 mr-2" />
                                    Send SMS
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>

                          {lead.case_type && (
                            <Badge variant="outline" className="text-xs mb-2">
                              {lead.case_type}
                            </Badge>
                          )}

                          {lead.contact?.email && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                              <Mail className="w-3 h-3" />
                              {lead.contact.email}
                            </div>
                          )}

                          {lead.contact?.phone && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
                              <Phone className="w-3 h-3" />
                              {lead.contact.phone}
                            </div>
                          )}

                          <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
                            <span>Source: {lead.source?.replace(/_/g, ' ')}</span>
                            {lead.consultation_count > 0 && (
                              <Badge variant="secondary" className="text-xs">
                                {lead.consultation_count} consult
                              </Badge>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* SMS Dialog */}
      <Dialog open={showSMSDialog} onOpenChange={setShowSMSDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-emerald-600" />
              Send SMS to {smsLead?.contact?.full_name}
            </DialogTitle>
          </DialogHeader>
          {smsLead && (
            <IntakeSMSComposer
              leadId={smsLead.id}
              contactName={smsLead.contact?.full_name}
              contactPhone={smsLead.contact?.phone}
              onMessageSent={() => {
                toast.success('SMS sent');
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* SMS Settings Dialog */}
      <Dialog open={showSMSSettings} onOpenChange={setShowSMSSettings}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-emerald-600" />
              Intake SMS Settings
            </DialogTitle>
            <DialogDescription>
              Configure SMS notifications and templates for prospective clients
            </DialogDescription>
          </DialogHeader>
          <IntakeSMSSettings />
        </DialogContent>
      </Dialog>
    </>
  );

  if (embedded) {
    return intakeBody;
  }

  return (
    <PullToRefresh onRefresh={handleRefresh} className="min-h-screen bg-slate-50">
      <div data-testid="intake-app-page">
        <TrialBanner />
        <header className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur">
          <div className="flex h-14 sm:h-16 items-center px-4 lg:px-8">
            <div className="flex items-center gap-2 mr-4 sm:mr-8">
              <Link to="/case-management?tab=intake" data-testid="back-to-hub">
                <Button variant="ghost" size="sm" className="h-9 px-2 text-muted-foreground hover:text-foreground">
                  <ChevronLeft className="w-4 h-4" />
                  <LayoutGrid className="w-4 h-4" />
                </Button>
              </Link>
              <div className="h-6 w-px bg-border hidden sm:block" />
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-md bg-emerald-600 flex items-center justify-center">
                  <UserPlus className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <span className="font-heading text-base sm:text-xl font-bold tracking-tight text-foreground">
                  Intake.AI
                </span>
              </div>
            </div>
            <div className="flex-1" />
            <div className="flex items-center gap-1 sm:gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowNewContactDialog(true)}>
                <Users className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">New Contact</span>
              </Button>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowNewLeadDialog(true)}>
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">New Lead</span>
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setShowSMSSettings(true)}>
                <MessageSquare className="w-4 h-4" />
              </Button>
              <Link to="/intake/settings">
                <Button variant="ghost" size="sm">
                  <Settings className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </header>
        {intakeBody}
      </div>
    </PullToRefresh>
  );
}
